import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { delftBlue, flame } from "@/theme/colors";

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox list" },
  { value: "yes-no-question", label: "Yes / No" },
  { value: "file-upload", label: "File upload" },
  { value: "signature", label: "Signature" },
  { value: "confirmation-checkbox", label: "Confirmation checkbox" },
];

const TYPES_WITH_OPTIONS = ["dropdown", "checkbox", "select", "radio", "multiselect"];

export const emptyFormConfig = (activityId) => ({
  formType: "prestart",
  version: "1.0.0",
  totalPages: 1,
  pages: [emptyPage(1)],
  submitConfig: {
    fieldMappings: [],
    additionalParams: activityId ? { activityId } : {},
  },
});

const emptyPage = (pageNumber) => ({
  pageNumber,
  title: `Page ${pageNumber}`,
  subtitle: "",
  sections: [emptySection()],
});

const emptySection = () => ({
  title: "Section",
  fields: [],
});

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const toFieldId = (label) => {
  const words = String(label || "")
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);
  if (!words.length) return "";
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) {
        return /^[0-9]/.test(lower) ? `field${lower.charAt(0).toUpperCase()}${lower.slice(1)}` : lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
};

const isGeneratedFieldId = (fieldId, label) => {
  const current = String(fieldId || "").trim();
  if (!current) return true;
  if (/^field_[a-z0-9]+$/i.test(current)) return true;
  return current === toFieldId(label);
};

const emptyField = () => {
  const label = "New question";
  return {
    id: toFieldId(label),
    label,
    type: "text",
    placeholder: "",
    validation: { required: false },
  };
};

const emptyOption = () => ({
  label: "Option",
  value: `option_${Math.random().toString(36).slice(2, 6)}`,
});

const matchingTextFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 51,
  },
};

const moveItem = (list, index, direction) => {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

const normalizeOptions = (options = []) =>
  options.map((option) =>
    typeof option === "string"
      ? { label: option, value: option }
      : { label: option?.label || option?.value || "", value: option?.value || option?.label || "" }
  );

const FormBuilder = ({ value, onChange, disabled = false }) => {
  const config = value && Array.isArray(value.pages) ? value : emptyFormConfig();
  const pages = config.pages || [];

  const commit = (nextPages, extra = {}) => {
    if (disabled) return;
    onChange({
      ...config,
      ...extra,
      pages: nextPages.map((page, index) => ({ ...page, pageNumber: index + 1 })),
      totalPages: nextPages.length,
    });
  };

  const updatePage = (pageIndex, patch) => {
    const next = clone(pages);
    next[pageIndex] = { ...next[pageIndex], ...patch };
    commit(next);
  };

  const updateSection = (pageIndex, sectionIndex, patch) => {
    const next = clone(pages);
    next[pageIndex].sections[sectionIndex] = {
      ...next[pageIndex].sections[sectionIndex],
      ...patch,
    };
    commit(next);
  };

  const updateField = (pageIndex, sectionIndex, fieldIndex, patch) => {
    const next = clone(pages);
    next[pageIndex].sections[sectionIndex].fields[fieldIndex] = {
      ...next[pageIndex].sections[sectionIndex].fields[fieldIndex],
      ...patch,
    };
    commit(next);
  };

  return (
    <Stack spacing={2}>
      {pages.map((page, pageIndex) => (
        <Accordion
          key={`page-${pageIndex}`}
          defaultExpanded={pageIndex === 0}
          sx={{ outline: `1px ${delftBlue[100]} solid`, borderRadius: "8px !important", "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", pr: 1 }}>
              <Typography sx={{ fontFamily: "Audiowide, sans-serif", color: delftBlue[500], flex: 1 }}>
                {page.title || `Page ${pageIndex + 1}`}
              </Typography>
              <IconButton type="button" size="small" disabled={disabled || pageIndex === 0} onClick={(e) => { e.stopPropagation(); commit(moveItem(pages, pageIndex, -1)); }}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton type="button" size="small" disabled={disabled || pageIndex === pages.length - 1} onClick={(e) => { e.stopPropagation(); commit(moveItem(pages, pageIndex, 1)); }}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                type="button"
                size="small"
                disabled={disabled || pages.length <= 1}
                onClick={(e) => {
                  e.stopPropagation();
                  commit(pages.filter((_, index) => index !== pageIndex));
                }}
              >
                <DeleteOutlineIcon fontSize="small" sx={{ color: flame[500] }} />
              </IconButton>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  size="small"
                  label="Page title"
                  value={page.title || ""}
                  disabled={disabled}
                  onChange={(e) => updatePage(pageIndex, { title: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Page subtitle"
                  value={page.subtitle || ""}
                  disabled={disabled}
                  onChange={(e) => updatePage(pageIndex, { subtitle: e.target.value })}
                  fullWidth
                />
              </Stack>

              {(page.sections || []).map((section, sectionIndex) => (
                <Box
                  key={`section-${pageIndex}-${sectionIndex}`}
                  sx={{ p: 2, borderRadius: 1.5, background: delftBlue[50] }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      label="Section title"
                      value={section.title || ""}
                      disabled={disabled}
                      onChange={(e) => updateSection(pageIndex, sectionIndex, { title: e.target.value })}
                      fullWidth
                    />
                    <IconButton
                      type="button"
                      size="small"
                      disabled={disabled || (page.sections || []).length <= 1}
                      onClick={() => {
                        const next = clone(pages);
                        next[pageIndex].sections = next[pageIndex].sections.filter((_, index) => index !== sectionIndex);
                        commit(next);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" sx={{ color: flame[500] }} />
                    </IconButton>
                  </Stack>

                  <Stack spacing={2}>
                    {(section.fields || []).map((field, fieldIndex) => (
                      <FieldEditor
                        key={`field-${pageIndex}-${sectionIndex}-${fieldIndex}`}
                        field={field}
                        disabled={disabled}
                        onChange={(patch) => updateField(pageIndex, sectionIndex, fieldIndex, patch)}
                        onMove={(direction) => {
                          const next = clone(pages);
                          next[pageIndex].sections[sectionIndex].fields = moveItem(
                            next[pageIndex].sections[sectionIndex].fields,
                            fieldIndex,
                            direction
                          );
                          commit(next);
                        }}
                        onDelete={() => {
                          const next = clone(pages);
                          next[pageIndex].sections[sectionIndex].fields = next[pageIndex].sections[sectionIndex].fields.filter(
                            (_, index) => index !== fieldIndex
                          );
                          commit(next);
                        }}
                        canMoveUp={fieldIndex > 0}
                        canMoveDown={fieldIndex < (section.fields || []).length - 1}
                      />
                    ))}
                    <Button
                      type="button"
                      startIcon={<AddIcon />}
                      disabled={disabled}
                      onClick={() => {
                        const next = clone(pages);
                        next[pageIndex].sections[sectionIndex].fields = [
                          ...(next[pageIndex].sections[sectionIndex].fields || []),
                          emptyField(),
                        ];
                        commit(next);
                      }}
                      sx={{ textTransform: "none", alignSelf: "flex-start", color: delftBlue[500] }}
                    >
                      Add question
                    </Button>
                  </Stack>
                </Box>
              ))}

              <Button
                type="button"
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() => {
                  const next = clone(pages);
                  next[pageIndex].sections = [...(next[pageIndex].sections || []), emptySection()];
                  commit(next);
                }}
                sx={{ textTransform: "none", alignSelf: "flex-start" }}
              >
                Add section
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Button
        type="button"
        startIcon={<AddIcon />}
        variant="outlined"
        disabled={disabled}
        onClick={() => commit([...pages, emptyPage(pages.length + 1)])}
        sx={{ textTransform: "none", borderColor: delftBlue[200], color: delftBlue[500] }}
      >
        Add page
      </Button>
    </Stack>
  );
};

const FieldEditor = ({
  field,
  disabled,
  onChange,
  onDelete,
  onMove,
  canMoveUp,
  canMoveDown,
}) => {
  const type = field.type || "text";
  const options = normalizeOptions(field.options);
  const usesQuestion = type === "yes-no-question";
  const labelValue = usesQuestion ? field.question || field.label || "" : field.label || field.question || "";

  return (
    <Box sx={{ p: 2, background: "white", borderRadius: 1.5, outline: `1px ${delftBlue[100]} solid` }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ flex: 1, fontFamily: "Montserrat, sans-serif", fontWeight: 600, color: delftBlue[600], fontSize: 14 }}>
            {labelValue || "Untitled question"}
          </Typography>
          <IconButton type="button" size="small" disabled={disabled || !canMoveUp} onClick={() => onMove(-1)}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton type="button" size="small" disabled={disabled || !canMoveDown} onClick={() => onMove(1)}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton type="button" size="small" disabled={disabled} onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" sx={{ color: flame[500] }} />
          </IconButton>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
          <TextField
            label={usesQuestion ? "Question" : "Label"}
            value={labelValue}
            disabled={disabled}
            onChange={(e) => {
              const nextLabel = e.target.value;
              const patch = usesQuestion
                ? { question: nextLabel, label: nextLabel }
                : { label: nextLabel };
              if (isGeneratedFieldId(field.id || field.fieldId, labelValue)) {
                const nextId = toFieldId(nextLabel);
                patch.id = nextId;
                patch.fieldId = nextId;
              }
              onChange(patch);
            }}
            fullWidth
            sx={matchingTextFieldSx}
          />
          <TextField
            select
            label="Type"
            value={FIELD_TYPES.some((item) => item.value === type) ? type : "text"}
            disabled={disabled}
            onChange={(e) => {
              const nextType = e.target.value;
              const patch = { type: nextType };
              if (nextType === "yes-no-question") {
                const text = field.question || field.label || "";
                patch.question = text;
                patch.label = text;
              }
              if (nextType === "file-upload" && field.multiple == null) {
                patch.multiple = true;
              }
              onChange(patch);
            }}
            sx={{ minWidth: 180 }}
          >
            {FIELD_TYPES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
            {!FIELD_TYPES.some((item) => item.value === type) && (
              <MenuItem value={type}>{type}</MenuItem>
            )}
          </TextField>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
          <TextField
            label="Field ID"
            value={field.id || field.fieldId || ""}
            disabled={disabled}
            onChange={(e) => onChange({ id: e.target.value, fieldId: e.target.value })}
            fullWidth
            sx={matchingTextFieldSx}
          />
          {type !== "yes-no-question" && type !== "confirmation-checkbox" && type !== "signature" && (
            <TextField
              label="Placeholder"
              value={field.placeholder || ""}
              disabled={disabled}
              onChange={(e) => onChange({ placeholder: e.target.value })}
              fullWidth
              sx={matchingTextFieldSx}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(field.validation?.required || field.required)}
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    validation: { ...(field.validation || {}), required: e.target.checked },
                    required: e.target.checked,
                  })
                }
              />
            }
            label="Required"
          />
          {type === "text" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(field.multiline)}
                  disabled={disabled}
                  onChange={(e) => onChange({ multiline: e.target.checked })}
                />
              }
              label="Multiline"
            />
          )}
        </Stack>

        {type === "number" && (
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="number"
              label="Min"
              value={field.validation?.min ?? ""}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  validation: {
                    ...(field.validation || {}),
                    min: e.target.value === "" ? undefined : Number(e.target.value),
                  },
                })
              }
            />
            <TextField
              size="small"
              type="number"
              label="Max"
              value={field.validation?.max ?? ""}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  validation: {
                    ...(field.validation || {}),
                    max: e.target.value === "" ? undefined : Number(e.target.value),
                  },
                })
              }
            />
          </Stack>
        )}

        {type === "yes-no-question" && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.showNotesInput !== false}
                  disabled={disabled}
                  onChange={(e) => onChange({ showNotesInput: e.target.checked })}
                />
              }
              label="Show notes input"
            />
            <TextField
              size="small"
              label="Note placeholder"
              value={field.notePlaceholder || ""}
              disabled={disabled}
              onChange={(e) => onChange({ notePlaceholder: e.target.value })}
            />
            <TextField
              size="small"
              label="Note helper text"
              value={field.noteLabel || ""}
              disabled={disabled}
              onChange={(e) => onChange({ noteLabel: e.target.value })}
            />
          </>
        )}

        {type === "file-upload" && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              label="Upload label"
              value={field.uploadLabel || ""}
              disabled={disabled}
              onChange={(e) => onChange({ uploadLabel: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(field.multiple)}
                  disabled={disabled}
                  onChange={(e) => onChange({ multiple: e.target.checked })}
                />
              }
              label="Allow multiple"
            />
          </Stack>
        )}

        {type === "signature" && (
          <TextField
            size="small"
            label="Signature label"
            value={field.signatureLabel || ""}
            disabled={disabled}
            onChange={(e) => onChange({ signatureLabel: e.target.value })}
          />
        )}

        {TYPES_WITH_OPTIONS.includes(type) && (
          <Box>
            <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 13, color: delftBlue[500], mb: 1 }}>
              Options
            </Typography>
            <Stack spacing={1}>
              {options.map((option, optionIndex) => (
                <Stack key={`${option.value}-${optionIndex}`} direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Label"
                    value={option.label}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = [...options];
                      next[optionIndex] = { ...next[optionIndex], label: e.target.value };
                      onChange({ options: next });
                    }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Value"
                    value={option.value}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = [...options];
                      next[optionIndex] = { ...next[optionIndex], value: e.target.value };
                      onChange({ options: next });
                    }}
                    fullWidth
                  />
                  <IconButton
                    type="button"
                    size="small"
                    disabled={disabled}
                    onClick={() => onChange({ options: options.filter((_, index) => index !== optionIndex) })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                type="button"
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() => onChange({ options: [...options, emptyOption()] })}
                sx={{ textTransform: "none", alignSelf: "flex-start" }}
              >
                Add option
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default FormBuilder;
